/**
 * The path's part of the shell, loaded only when «Путь пешки» 2.0 is on (../features/path/shell.ts, fixer r1): the
 * first load of a child without the path carries none of the path's code. `usePathShell(on)` is the module once it is
 * here (null before and when the path is off); the App waits for it before a screen that depends on the path.
 */
import { useEffect, useMemo, useState } from 'react';
import type * as Shell from '../features/path/shell.ts';
import type { PathHomeInput, PathHomeView } from '../features/path/daily/usePathHome.ts';

export type PathShell = typeof Shell;

let loaded: PathShell | null = null;
let loading: Promise<PathShell> | null = null;

/** The path's shell module (one request, shared; a failed one is asked again by the next call). */
export function loadPathShell(): Promise<PathShell> {
  loading ??= import('../features/path/shell.ts').then(
    (m) => {
      loaded = m;
      return m;
    },
    (error: unknown) => {
      loading = null;
      throw error;
    },
  );
  return loading;
}

/** a failed load of the shell is tried again after this (a dropped network; a chunk of an old build reloads the page) */
export const PATH_SHELL_RETRY_MS = 3_000;

/** The module now, if it has come (outside React). */
export function pathShellNow(): PathShell | null {
  return loaded;
}

export function usePathShell(on: boolean): PathShell | null {
  const [shell, setShell] = useState<PathShell | null>(loaded);
  // the App waits for the shell before a path screen: a failed load must not leave the spinner forever
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    if (!on || shell !== null) return;
    let alive = true;
    let retry: ReturnType<typeof setTimeout> | undefined;
    loadPathShell().then(
      (m) => {
        if (alive) setShell(m);
      },
      () => {
        if (alive) retry = setTimeout(() => setAttempt((n) => n + 1), PATH_SHELL_RETRY_MS);
      },
    );
    return () => {
      alive = false;
      if (retry !== undefined) clearTimeout(retry);
    };
  }, [on, shell, attempt]);
  return on ? shell : null;
}

/**
 * The home's path view (features/path/daily/usePathHome.ts) over the lazily loaded shell: null while the path is off or
 * its code has not come yet; recomputed on the route, the inputs and whatever the path's watcher reports.
 */
export function usePathHomeLazy(shell: PathShell | null, { on, routeKey, stage, games, reviewed, puzzles = 0 }: PathHomeInput): PathHomeView | null {
  const [tick, setTick] = useState(0);
  const home = routeKey === '#/' || routeKey === '';
  useEffect(() => (on && shell !== null ? shell.watchPathHome({ home, onChange: () => setTick((n) => n + 1) }) : undefined), [on, home, shell]);
  return useMemo(
    () => (on && shell !== null ? shell.pathHomeOf({ stage, games, reviewed, puzzles }) : null),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [on, shell, routeKey, stage, games, reviewed, puzzles, tick],
  );
}
